import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import FAQAccordion from '../Services/FAQAccordion';
import Seo from '../Seo';

const SolutionContract = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);



  const faqs = [
    {
      q: "When should I use Contract Staffing instead of Full-time?",
      a: "Contract staffing is ideal when business needs are time-bound, project-specific, or require specialized skills that are not needed permanently. It helps teams fill capability gaps quickly during transformation initiatives, product launches, and peak workload periods. Compared with full-time hiring, contract models reduce long-cycle recruitment dependency for urgent delivery requirements. It is also effective for validating role criticality before committing to long-term headcount expansion. In short, contract staffing gives organizations speed and flexibility while maintaining delivery momentum."
    },
    {
      q: "Can a contractor transition into a permanent employee?",
      a: "Yes, contract-to-hire is a practical path when organizations want to evaluate performance in a live environment before permanent commitment. It allows managers to assess technical quality, collaboration style, and execution consistency against real project outcomes. This reduces hiring risk by replacing assumption-based interviews with observable role performance. Transition planning includes compensation alignment, onboarding continuity, and stakeholder approvals to avoid disruption. The model is especially useful for hard-to-fill roles where long-term fit is critical."
    },
    {
      q: "Do you handle all payroll liabilities?",
      a: "Yes. We support employer-of-record responsibilities for contract engagements, including payroll administration, documentation workflows, and compliance coordination. This reduces operational load for internal teams and improves governance consistency across high-volume staffing programs. We also align onboarding and engagement controls with your project and security requirements to keep execution stable. Regular reporting provides visibility into active resources, costs, and compliance status. This ensures contract staffing remains efficient, controlled, and audit-ready."
    },
    {
      q: "How do you match contractors to highly specialized project needs?",
      a: "We start with role calibration workshops to define technical depth, delivery context, and team collaboration requirements in practical terms. Candidate evaluation then combines skill validation, relevant domain exposure, and availability alignment for immediate execution. This prevents generic profile submissions and increases interview-to-selection efficiency. We also account for project phase needs, such as build, migration, stabilization, or optimization stages. The result is stronger role fit and faster productivity after onboarding."
    },
    {
      q: "What controls are in place for contract performance management?",
      a: "Performance management is supported through clear role expectations, milestone accountability, and regular manager feedback loops. We track delivery continuity and escalation signals early so corrective action can be taken before project impact grows. Governance reporting provides visibility into utilization, continuity risk, and engagement status for decision-makers. If replacement or skill adjustment is needed, we coordinate transitions with minimal workflow disruption. This keeps contract delivery dependable throughout the engagement lifecycle."
    }
  ];

  return (
    <div className="bg-white min-h-screen pt-4 [&_.text-h1]:text-h2 [&_.text-h2]:text-h3 [&_.text-body]:text-small [&_.text-small]:text-subtext">
      <Seo
        title="Contract Positions"
        description="Libsys contract positions — deploy project-ready contract professionals quickly, contract-to-hire flexibility, and payroll/compliance support for time-bound initiatives."
        path="/solutions/contract"
      />
      {/* ══ HERO SECTION ══════════════════════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-6">
        <div className="rounded-[2.5rem] border border-[#236086]/10 bg-slate-50/70 p-6 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-red-tagline mb-5">
                On-Demand Contract Staffing
              </span>
              <h1 className="text-h1 text-primary mb-6">
                Inject Specialized Expertise Exactly <span className="text-secondary">Where You Need It</span>
              </h1>
              <p className="text-slate-600 text-body leading-[1.8] font-medium mb-8 max-w-2xl">
                When a project hits a critical milestone, you don't have months to navigate traditional recruitment cycles. Libsys provides immediate access to high-caliber contract professionals who are ready to hit the ground running. Whether you need a niche full-stack developer, a certified Cloud Architect, or a specialized QA Lead, our contractors integrate seamlessly into your existing workflows and culture to maintain your project velocity. We help organizations handle peak demand and technical complexity through rapid fulfillment, structured onboarding, and rigorous performance governance tailored for project-based execution.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-10">
                {[
                  'Rapid contract fulfillment cycles',
                  'Niche tech stack specialization',
                  'Contract-to-hire transition paths',
                  'Full payroll & liability management',
                  'Immediate onboarding readiness',
                  'SLA-backed delivery governance'
                ].map((item) => (
                  <li key={item} className="text-slate-700 text-small font-bold leading-relaxed flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-[7px] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+16307991556"
                  className="inline-flex items-center gap-3 px-10 py-5 bg-primary hover:bg-secondary text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-xl active:scale-95 group"
                >
                  Request Specialized Talent
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-7 lg:-mt-12">
              <div className="rounded-[2.5rem] border border-slate-200 shadow-[0_30px_70px_-15px_rgba(35,96,134,0.2)] bg-slate-100 relative group overflow-hidden">
                <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
                 <img
                  src="/contract1.png"
                  alt="Contract staffing simulation"
                  className="w-full h-[400px] lg:h-[600px] object-cover group-hover:scale-105 transition-transform duration-1000"
                  onError={(e) => { e.currentTarget.src = '/hero_tech_office.png'; }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ AGILE PILLARS SECTION ═════════════════════════════════════════════ */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-h2 text-primary mb-6">Staffing for Technical Momentum</h2>
            <p className="text-slate-600 font-medium leading-relaxed">
              We empower your teams to handle volume volatility and niche technical requirements without the overhead of permanent headcount expansion.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "Technical Precision", desc: "Our vetting process is calibrated for 'Day 1' impact, ensuring contractors possess the exact skill sets required for your project phase." },
              { title: "Delivery Elasticity", desc: "Quickly ramp teams up for transformation sprints or product launches, and scale down seamlessly as project goals are achieved." },
              { title: "Operational Safety", desc: "We manage all employment liabilities, payroll, and compliance, providing a low-risk framework for large-scale external workforces." }
            ].map((pillar, i) => (
              <div key={i} className="p-10 rounded-[2rem] bg-[#e2edf5] border border-[#236086]/10 hover:shadow-xl transition-all">
                <h4 className="text-xl font-black text-primary mb-4">{pillar.title}</h4>
                <p className="text-slate-600 text-body leading-relaxed font-medium">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CORE VALUE ══════════════════════════════════════════════════════ */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-red-tagline block mb-4">Project Velocity</span>
              <h2 className="text-h2 text-primary mb-6">Frictionless Hiring for Modern Projects</h2>
              <p className="text-slate-600 text-small mb-8 leading-[1.8] font-medium">
                Our contract staffing philosophy is centered on the rapid injection of high-value technical expertise. We understand that in modern enterprise environments, missing a resource often means missing a market window. By maintaining curated pipelines of pre-screened specialists across North America and India, we enable you to scale your delivery capacity in record time. Our model includes end-to-end engagement governance, from initial requirement calibration to payroll and compliance management, ensuring your project remains stable and high-performing throughout its lifecycle.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 mb-10">
                {[
                  { title: "Instant Access", desc: "Shorten fulfillment cycles for urgent technical mandates." },
                  { title: "Dual Delivery", desc: "Onshore & offshore contract options for 24/7 momentum." },
                  { title: "Ready-Now Pool", desc: "Professionals vetted for specific frameworks and tools." },
                  { title: "Full Compliance", desc: "Employer-of-record services to minimize operational risk." }
                ].map((item, i) => (
                  <div key={i} className="space-y-2.5">
                    <div className="text-secondary flex items-center gap-2">
                      <span className="font-black text-primary text-tiny uppercase tracking-widest">{item.title}</span>
                    </div>
                    <p className="text-slate-600 text-subtext leading-relaxed font-semibold">{item.desc}</p>
                  </div>
                ))}
              </div>
              <ul className="space-y-4">
                {[
                  'Coverage for hard-to-fill niche roles (SAP, Cloud, Cybersecurity)',
                  'Seamless contract-to-hire transition paths for long-term fit',
                  'Rigorous performance monitoring to ensure project continuity'
                ].map((item) => (
                  <li key={item} className="text-slate-800 text-subtext font-bold leading-relaxed flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
               <div className="rounded-[2.5rem] overflow-hidden border border-[#236086]/10 shadow-2xl bg-white p-2">
                 <img
                  src="/contract2.png"
                  alt="Contract workforce operations"
                  className="w-full h-auto aspect-square lg:aspect-[4/3] object-cover rounded-[2.2rem]"
                  onError={(e) => { e.currentTarget.src = '/hero_tech_office.png'; }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ CTA SECTION ═══════════════════════════════════════════════════════ */}
      <section className="py-12 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="bg-primary rounded-[3rem] p-8 lg:py-10 lg:px-12 relative overflow-hidden text-center lg:text-left">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/10 rounded-full blur-2xl -ml-20 -mb-20 pointer-events-none" />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <span className="text-blue-100/60 text-tiny font-black uppercase tracking-[0.4em] mb-2 block">Engagement Options</span>
              <h2 className="text-3xl lg:text-4xl font-black text-white mb-4 leading-tight">
                Flexible Technical <span className="text-secondary">Contracting</span>
              </h2>
              <p className="text-blue-100/80 text-lg mb-6 max-w-2xl font-medium">
                Bridge specialized infrastructure and development gaps with governed, high-performance contractor talent. Maintain project momentum with absolute agility and uncompromised quality.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <a
                  href="tel:+16307991556"
                  className="px-10 py-5 bg-white text-primary font-black rounded-xl hover:bg-blue-50 transition-all flex items-center gap-3 uppercase tracking-widest text-subtext shadow-2xl active:scale-95"
                >
                  630-799-1556
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="grid grid-cols-1 gap-3">
                {[
                  { k: 'Stability', v: 'Governed Delivery Quality' },
                  { k: 'Precision', v: 'Role-Aligned Sourcing' },
                  { k: 'Agility', v: 'Rapid Capacity Scaling' }
                ].map((item) => (
                  <div key={item.k} className="bg-white/10 backdrop-blur-md border border-white/10 p-4 rounded-2xl text-left">
                    <p className="text-blue-100/50 text-[10px] font-black uppercase tracking-widest">{item.k}</p>
                    <p className="text-white text-small font-bold mt-1">{item.v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={faqs} />
    </div>
  );
};

export default SolutionContract;
